import React from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Full tenant provisioning dialog: base info + optional admin + triage manager
 * (CogniSeg-global vs client-scoped). Slug is auto-derived from name in parent.
 * Parent owns the `formData` / `plans` state and the submit handler.
 */
export const CreateTenantDialog = ({
  open, onOpenChange, formData, setFormData, plans, saving, onSubmit, generateSlug,
}) => {
  const clientTriageValid = formData.triage_mode !== 'client' ||
    (formData.triage_name && formData.triage_email && formData.triage_password);
  const canSubmit = !saving && formData.name && formData.slug && clientTriageValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-50 border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Criar Novo Cliente</DialogTitle>
          <DialogDescription className="text-slate-500">
            Configure os dados do novo tenant e opcionalmente crie um administrador
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Nome da Empresa</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev, name: e.target.value, slug: generateSlug(e.target.value),
                }))}
                placeholder="Empresa XYZ"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="tenant-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-600">Slug (URL)</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase() }))}
                placeholder="empresa-xyz"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="tenant-slug-input"
              />
              <p className="text-xs text-slate-500 mt-1">/{formData.slug || 'slug'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600">Domínio (opcional)</Label>
              <Input
                value={formData.domain || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                placeholder="empresa.com"
                className="bg-white border-slate-300 text-slate-900"
              />
            </div>
            <div>
              <Label className="text-slate-600">Plano</Label>
              <Select value={formData.plan} onValueChange={(v) => setFormData(prev => ({ ...prev, plan: v }))}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="new-tenant-plan-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-300">
                  {plans.map(plan => (
                    <SelectItem key={plan.key} value={plan.key} className="text-slate-900 hover:bg-slate-700">
                      {plan.name} ({plan.max_users === -1 ? '∞' : plan.max_users} users, {plan.max_reports_month === -1 ? '∞' : plan.max_reports_month}/mês)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Catálogo sincronizado com o SaaS-Hub. Para adicionar novos planos, use a página de <a href="/super-admin/planos" className="text-indigo-700 hover:underline">Planos</a>.
              </p>
            </div>
          </div>

          <Separator className="bg-slate-700" />

          <div>
            <Label className="text-base font-medium text-slate-900">Administrador Inicial</Label>
            <p className="text-sm text-slate-500 mb-3">Criar automaticamente um utilizador administrador</p>

            <div className="space-y-3">
              <Input
                value={formData.admin_name}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_name: e.target.value }))}
                placeholder="Nome do Administrador"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="tenant-admin-name-input"
              />
              <Input
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_email: e.target.value }))}
                placeholder="admin@empresa.com"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="tenant-admin-email-input"
              />
              <Input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData(prev => ({ ...prev, admin_password: e.target.value }))}
                placeholder="Password inicial"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="tenant-admin-password-input"
              />
            </div>
          </div>

          <Separator className="bg-slate-700" />

          <div>
            <Label className="text-base font-medium text-slate-900">Gestor de Triagem (obrigatório)</Label>
            <p className="text-sm text-slate-500 mb-3">Utilizador que recebe todas as notificações do fluxo (novas denúncias, prazos, mudanças de estado).</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${formData.triage_mode === 'cogniseg' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white'}`}
                data-testid="triage-mode-cogniseg-option"
              >
                <input
                  type="radio" name="triage_mode" value="cogniseg"
                  checked={formData.triage_mode === 'cogniseg'}
                  onChange={() => setFormData(prev => ({ ...prev, triage_mode: 'cogniseg' }))}
                  className="mt-1"
                />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Serviço CogniSeg</div>
                  <div className="text-slate-500">Cria o utilizador global <code className="text-xs">gestortriagem@cogniseg.com</code> com password definida em Definições da Plataforma &gt; CogniSeg.</div>
                </div>
              </label>
              <label
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${formData.triage_mode === 'client' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white'}`}
                data-testid="triage-mode-client-option"
              >
                <input
                  type="radio" name="triage_mode" value="client"
                  checked={formData.triage_mode === 'client'}
                  onChange={() => setFormData(prev => ({ ...prev, triage_mode: 'client' }))}
                  className="mt-1"
                />
                <div className="text-sm">
                  <div className="font-medium text-slate-900">Gestor do próprio cliente</div>
                  <div className="text-slate-500">Um utilizador do cliente é criado com o role Triage Manager. Introduza os dados abaixo.</div>
                </div>
              </label>
            </div>

            {formData.triage_mode === 'client' && (
              <div className="space-y-3">
                <Input
                  value={formData.triage_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, triage_name: e.target.value }))}
                  placeholder="Nome do Gestor de Triagem"
                  className="bg-white border-slate-300 text-slate-900"
                  data-testid="tenant-triage-name-input"
                />
                <Input
                  type="email"
                  value={formData.triage_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, triage_email: e.target.value }))}
                  placeholder="gestor.triagem@empresa.com"
                  className="bg-white border-slate-300 text-slate-900"
                  data-testid="tenant-triage-email-input"
                />
                <Input
                  type="password"
                  value={formData.triage_password}
                  onChange={(e) => setFormData(prev => ({ ...prev, triage_password: e.target.value }))}
                  placeholder="Password inicial (mín. 8 caracteres)"
                  className="bg-white border-slate-300 text-slate-900"
                  data-testid="tenant-triage-password-input"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-700">
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-indigo-700 hover:bg-amber-600 text-black"
            data-testid="create-tenant-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Criar Cliente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
